import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { restartableTask, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type Patient from 'parog-web/models/patient';
import type AnalyticsService from 'parog-web/services/analytics';
import type FetcherService from 'parog-web/services/fetcher';
import { apiFieldset } from 'parog-web/utils/api-fieldset';

const MINIMUM_QUERY_LENGTH = 2;

interface SearchMeta {
  'search-criteria-types'?: string[];
  'search-criteria-count'?: number;
}

export default class PatientSearchComponent extends Component {
  @service declare analytics: AnalyticsService;
  @service declare fetcher: FetcherService;

  queryPatients = restartableTask(async (event: Event) => {
    const query = (event.target as HTMLInputElement).value;
    if (query.trim().length < MINIMUM_QUERY_LENGTH) return [];

    // Bounce search
    await timeout(400);

    const patients = (await this.fetcher.query('patient', {
      filter: { query: query },
      fields: apiFieldset('patient', ['firstName', 'lastName', 'birthName'])
    })) as unknown as Patient[] & { meta: SearchMeta | null };
    const meta = patients.meta;

    this.analytics.trackAction([
      'Recherche Patient Effectuée',
      {
        number_results: patients.length,
        search_criteria_types: meta?.['search-criteria-types'],
        search_criteria_count: meta?.['search-criteria-count']
      }
    ]);

    return patients;
  });

  <template>
    <div class='field m-auto'>
      <div class='ui {{if this.queryPatients.isRunning "loading"}} icon input'>
        <input
          type='text'
          placeholder='Rechercher un patient'
          aria-label='Rechercher un patient'
          {{on 'input' (perform this.queryPatients)}}
        />
        <i class='search icon'></i>

        <div
          aria-label='Résultats de la recherche'
          class='{{unless this.queryPatients.lastSuccessful.value "hidden"}}
            absolute top-[38px] max-h-[212.8px] overflow-y-auto overflow-x-hidden rounded-sm border border-solid border-[rgba(0,0,0,0.2)] bg-white [&_a:hover]:bg-[rgba(0,0,0,0.1)] [&_a]:block [&_a]:px-[0.5em] [&_a]:py-[0.7em] [&_a_.extra]:block [&_a_.extra]:text-[#808080]'
        >
          {{#each this.queryPatients.lastSuccessful.value as |p|}}
            <LinkTo
              @route='medics-web.protected.patients.patient'
              @model={{p.id}}
            >
              <i class='user circle icon'></i>{{p.fullNameWithoutBirthName}}
              {{#if p.birthName}}
                <span class='ui extra small text'>
                  Nom de naissance :
                  {{p.birthName}}
                </span>
              {{/if}}
            </LinkTo>
          {{/each}}
        </div>
      </div>
    </div>
  </template>
}
